import { useMemo, useState } from "react";
import { Box, Center, Checkbox, Spinner, VStack } from "@chakra-ui/react";

import { useRequestParams } from "contexts/RequestParamsProvider";
import { DocumentsFilter, FilterActionButtons } from "../DocumentsFilter";
import { GenericFilterProps } from "./generic-filter-props";

export type FilterOption = { label: string; value: string };

type CheckboxListFilterProps = {
  filterOptions: Array<FilterOption>;
  isLoading?: boolean;
  maxH?: string;
} & GenericFilterProps;

const CheckboxListPickerAndActionButtons = ({
  filterKey,
  filterOptions,
  filter,
  isLoading,
  maxH,
}: {
  filterKey: string;
  filterOptions: Array<{ label: string; value: string }>;
  filter?: string[];
  isLoading?: boolean;
  maxH: string;
}) => {
  const { onChangeSelect } = useRequestParams();
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: boolean;
  }>(() =>
    // map the saved filter to our state object that contains [filter]: boolean
    (filter ?? []).reduce((obj, filter) => ({ ...obj, [filter]: true }), {})
  );

  // map the filter object to an array of selected filters that the filter context will use
  const selectedFilterState = useMemo(
    () =>
      Object.entries(selectedFilters)
        .filter(([, value]) => value)
        .map(([filter]) => filter)
        .sort((a, b) => a.localeCompare(b)),
    [selectedFilters]
  );

  const onSet = () => {
    onChangeSelect(filterKey, selectedFilterState);
  };

  const onClear = () => {
    setSelectedFilters({});
    onChangeSelect(filterKey, undefined);
  };

  return (
    <>
      {isLoading ? (
        <Center flex={1} minH="200px">
          <Spinner />
        </Center>
      ) : (
        <VStack
          mb={6}
          gap={0}
          alignItems="flex-start"
          overflowY="auto"
          maxH={maxH}
        >
          {filterOptions.map((filterOption) => (
            <Checkbox
              key={filterOption.value}
              isChecked={
                filterOption && filterOption.value
                  ? selectedFilters[filterOption.value]
                  : false
              }
              onChange={(e) =>
                setSelectedFilters((current) => ({
                  ...current,
                  [filterOption.value]: e.target.checked,
                }))
              }
              px={6}
              py={1}
              w="full"
              _hover={{ bg: "navy.50" }}
            >
              <Box as="span" fontSize="sm">
                {filterOption.label}
              </Box>
            </Checkbox>
          ))}
        </VStack>
      )}

      <FilterActionButtons
        onReset={onClear}
        isResetDisabled={
          selectedFilterState.length === 0 && (filter ?? []).length === 0
        }
        onSet={onSet}
        isSetDisabled={
          selectedFilterState.length === (filter?.length ?? 0) &&
          selectedFilterState.every((v, i) => v === filter?.[i])
        }
      />
    </>
  );
};

export const CheckboxListFilter = ({
  filterKey,
  variant,
  name,
  filterOptions,
  isLoading,
  maxH,
}: CheckboxListFilterProps) => {
  const { filters } = useRequestParams();
  const filter = filters[filterKey] as string[] | undefined;

  const isSet = !!filter && filter.length > 0;

  return (
    <DocumentsFilter
      name={name}
      buttonName={isSet ? `${name} (${filter.length})` : name}
      isSet={isSet}
      variant={variant}
    >
      <CheckboxListPickerAndActionButtons
        {...{
          filter,
          filterOptions,
          filterKey,
          isLoading,
          maxH: maxH ?? "440px",
        }}
      />
    </DocumentsFilter>
  );
};
